import { useEffect, useMemo, useState } from 'react';

import lang from '../../../lib/language';
import { getUsRegularSessionBounds, getUsSessionKind } from '../../../lib/store/market-data/charting/dailySession.helpers';
import { ChartDataRange, ChartDataSet } from '../../../lib/store/market-data/charting/types';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { FeatureFlags } from '../../../lib/util/flags/types';
import { useLineClose } from '../../../lib/util/hooks/TradingDataHooks';
import { NullableNumber } from '../../../lib/util/types';

import AfterHoursIcon from '../../../assets/img/marketStates/after-hours.svg';
import OpenMarketIcon from '../../../assets/img/marketStates/open-market.svg';
import PreMarketIcon from '../../../assets/img/marketStates/pre-market.svg';
import { useFlags } from '../../../hooks';
import { CanvasChart } from '../../../shared_components/CanvasChart';

import { formatTooltipPrice } from './tooltipPrice.helpers';

import styles from './ChartContainer.module.scss';

type Props = {
  dataSet?: ChartDataSet;
  symbol: string;
  range: ChartDataRange;
  companyName?: string;
}

const INTRADAY_MULTI_DAY_RANGES: ChartDataRange[] = [ '1W', '1M' ];

const SESSION_ICONS = {
  preMarket: PreMarketIcon,
  regular: OpenMarketIcon,
  afterHours: AfterHoursIcon,
};

export const ChartContainer = ({ dataSet, symbol, range, companyName }: Props) => {
  const [lineClose, setLineClose] = useState<NullableNumber>(TradePriceCache.get(symbol)?.priceData.lineClose);
  useLineClose(symbol, lineClose, setLineClose, 'SymbolDetails/ChartContainer');

  const flags = useFlags([FeatureFlags.use_enhanced_charts]);
  const isEnhanced = flags[FeatureFlags.use_enhanced_charts].enabled;

  const showNoData = !dataSet?.lineData.length || !lineClose || !dataSet?.isValid;

  // The bounds follow the newest candle, so a window spanning two sessions resolves to the newest
  const regularSession = useMemo(() => {
    if (!isEnhanced || range !== '1D') return undefined;

    const lastCandle = dataSet?.rawData?.[dataSet.rawData.length - 1];
    return getUsRegularSessionBounds(lastCandle?.dateAndTime);
  }, [isEnhanced, range, dataSet]);

  const showsMultiDaySessionIcon = isEnhanced && INTRADAY_MULTI_DAY_RANGES.includes(range);

  useEffect(() => {
    const tradePrice = TradePriceCache.get(symbol);
    setLineClose(tradePrice?.priceData.lineClose);
  }, [symbol]);

  if (showNoData) {
    return (
      <div className={styles.noData}>
        <div className={styles.noDataText}>{lang.commonChartNoData()}</div>
        <div className={styles.backgroundImage}></div>
      </div>
    );
  }

  return (
    <div className={styles.rootContainerStyle}>
      <CanvasChart
        dataSet={dataSet}
        range={range}
        lineClose={lineClose}
        symbol={symbol}
        companyName={companyName}
        isEnhanced={isEnhanced}
        dailyMaxPoints={dataSet.dailyMaxPoints}
        regularSession={regularSession}
        sessionIcons={isEnhanced ? SESSION_ICONS : undefined}
        resolveSessionKind={showsMultiDaySessionIcon ? getUsSessionKind : undefined}
        formatTooltipPrice={formatTooltipPrice}
      />
    </div>
  );
};
